<template>
  <div class="w-full h-screen flex flex-col">
    <!-- Header -->
    <header class="flex justify-between items-center p-4 ods-panel mx-4 mt-4">
      <div class="flex items-center">
        <i class="fas fa-desktop text-accent-blue text-2xl ml-3"></i>
        <h1 class="text-2xl font-bold font-tajawal">
          <span>{{ selectedRestaurantName }}</span>
          <span class="text-accent-blue">محطة إدارة الطلبات</span>
        </h1>
      </div>
      <div class="flex items-center space-x-4">
        <div class="text-sm ods-tag !h-9 px-4 mr-2">
          <span>Status: </span>
          <span :class="connectionClass">{{ connectionStatus }}</span>
        </div>
        <div class="text-sm ods-tag !h-9 px-4 mr-2">
          <span>Tables: </span>
          <span class="text-accent-blue">{{ tableCounts.total }}</span>
        </div>
        <button @click="changeTheme" class="ods-btn !h-10 !w-10 !px-0">
          <i class="fas fa-sun" :class="{'hidden': isDarkMode}"></i>
          <i class="fas fa-moon" :class="{'hidden': !isDarkMode}"></i>
        </button>
        <button @click="toggleLanguage" class="ods-btn !h-10 !w-10 !px-0 text-sm">
          <span :class="{'hidden': language !== 'ar'}">AR</span>
          <span :class="{'hidden': language !== 'de'}">DE</span>
        </button>
        <button @click="toggleSound" class="ods-btn !h-10 !w-10 !px-0">
          <i class="fas fa-volume-up" :class="{'text-blue-500': soundEnabled}"></i>
        </button>
        <button @click="goBack" class="ods-btn !h-10 !w-10 !px-0">
          <i class="fas fa-arrow-left"></i>
        </button>
        <button @click="logOut" class="ods-btn ods-btn--danger !h-10 !w-10 !px-0 ml-2">
          <i class="fas fa-sign-out-alt"></i>
        </button>
      </div>
    </header>
    
    <!-- Tables Grid Layout -->
    <div class="flex-1 p-4 overflow-hidden">
      <div class="ods-panel h-full overflow-hidden flex flex-col">
        <div class="flex justify-between items-center p-3 bg-accent-blue text-white font-bold sticky top-0 z-10">
          <div class="flex items-center">
            <i class="fas fa-table ml-2"></i>
            <h2 class="text-lg font-tajawal">نظرة عامة على الطاولات</h2>
          </div>
          <div class="flex space-x-2">
            <div class="text-xs px-2 py-1 rounded" style="background: rgba(255,255,255,.25)">{{ tableCounts.active }}</div>
            <select v-model="tableFilter" class="text-sm p-1 rounded border" style="background: rgba(255,255,255,.18); color:#fff; border-color: rgba(255,255,255,.35)">
              <option value="all">{{ $t('all') }}</option>
              <option value="active">{{ $t('active') }}</option>
              <option value="takeaway">{{ $t('takeaway') }}</option>
            </select>
          </div>
        </div>
        <div v-if="loading" class="flex-1 flex items-center justify-center">
          <div class="text-center">
            <div class="animate-spin w-10 h-10 border-t-2 border-blue-500 rounded-full mx-auto mb-4"></div>
            <p style="color: var(--ods-ink-3)">Loading tables...</p>
          </div>
        </div>
        <div v-else-if="Object.keys(filteredTables).length === 0" class="flex-1 flex items-center justify-center">
          <div class="text-center py-10 font-bold" style="color: var(--ods-ink-3)">
            <p>{{ $t('noTables') }}</p>
          </div>
        </div>
        <div v-else class="flex-1 overflow-y-auto tables-grid">
          <!-- Small Square Box Cards -->
          <div 
            v-for="(orders, tableNumber) in filteredTables" 
            :key="tableNumber"
            class="table-card" 
            :class="[
              getTableStatusClass(orders),
              hasNewOrders(orders) ? 'new-card-pulse' : ''
            ]"
            @click="showTableDetails(tableNumber)"
          >
            <div class="flex justify-between mb-1">
              <div class="table-number">
                {{ tableNumber === 'takeaway' ? 'سفري' : tableNumber }}
              </div>
              <div v-if="hasNewOrders(orders)" class="new-badge">جديد</div>
            </div>
            
            <div class="waiter-info">
              <i class="fas fa-user"></i> {{ getWaiterName(orders[0].order) }}
            </div>
            
            <div class="order-status text-center mt-2">
              {{ getOrderStatusText(orders) }}
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Order Details Modal -->
    <div v-if="showModal" class="modal" @click="closeModal">
      <div class="modal-content" @click.stop>
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-xl font-bold">{{ modalTitle }}</h3>
          <button @click="closeModal" class="text-2xl" style="color: var(--ods-ink-2)">&times;</button>
        </div>
        <div v-if="selectedTable && tables[selectedTable]">
          <div 
            v-for="({ orderId, order }, index) in tables[selectedTable]" 
            :key="orderId"
            class="order-detail"
          >
            <div class="flex justify-between items-center">
              <div class="text-xl font-bold">{{ $t('orderNumber') }} {{ order.orderNumber || orderId.substring(0, 6) }}</div>
              <div class="text-sm" style="color: var(--ods-ink-3)">{{ formatTime(order.timestamp) }}</div>
            </div>
            
            <div class="mt-4">
              <!-- Sections by station -->
              <template v-for="(section, sectionKey) in getSectionsWithItems(order)">
                <div :key="sectionKey" :class="`station-group station-group-${sectionKey} mb-4`">
                  <div :class="`station-header station-header-${sectionKey}`">
                    <i :class="getSectionIcon(sectionKey) + ' ml-2'"></i>{{ getStationName(sectionKey) }}
                    <div 
                      :class="`status-badge ml-2 ${getSectionStatusClass(section.status || 'New')}`"
                    >
                      {{ getStatusText(section.status || 'New') }}
                    </div>
                  </div>
                  <div class="mt-2">
                    <div 
                      v-for="(item, itemIndex) in section.items" 
                      :key="`${sectionKey}-${itemIndex}`"
                      class="p-2 border-b last:border-b-0" style="border-color: var(--ods-line)"
                    >
                      <div class="flex items-start">
                        <span class="ml-2 text-lg font-bold text-yellow-500">{{ item.quantity || 1 }}x</span>
                        <div class="flex-1">
                          <div class="flex justify-between">
                            <div class="text-lg">
                              {{ getItemName(item) }}
                              <span v-if="item.articleNumber" class="text-xs ml-1" style="color: var(--ods-ink-3)">#{{ item.articleNumber }}</span>
                            </div>
                            <div 
                              :class="`status-badge ${getSectionStatusClass(item.status || section.status || 'New')} ml-2`"
                            >
                              {{ getStatusText(item.status || section.status || 'New') }}
                            </div>
                          </div>
                          <div v-if="item.notes" class="text-yellow-500 text-sm">{{ item.notes }}</div>
                          <div v-if="item.addedAt" class="text-green-400 text-xs mt-1">
                            <i class="fas fa-plus-circle mr-1"></i>{{ $t('addedAt') }} {{ formatTime(item.addedAt) }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </div>
            
            <!-- Order notes -->
            <div v-if="order.notes" class="mt-4 p-3 rounded" style="background: var(--ods-prep-tint); border-left: 3px solid var(--ods-prep)">
              <div class="text-yellow-500 font-bold">{{ $t('notes') }}:</div>
              <div>{{ order.notes }}</div>
            </div>
            
            <!-- Timeline of order changes -->
            <div v-if="getOrderTimeline(order).length > 1" class="mt-6">
              <h4 class="text-lg font-bold mb-3">{{ $t('orderTimeline') }}</h4>
              <div class="order-timeline">
                <div 
                  v-for="(event, eventIndex) in getOrderTimeline(order)" 
                  :key="eventIndex"
                  class="timeline-item"
                >
                  <div class="timeline-time">{{ formatTime(event.time) }}</div>
                  <div class="timeline-content">
                    {{ event.description }}
                    <span 
                      v-if="event.section" 
                      :class="`text-xs ml-2 station-header-${event.section}`"
                    >
                      ({{ getStationName(event.section) }})
                    </span>
                  </div>
                </div>
              </div>
            </div>
            
            <hr v-if="index < tables[selectedTable].length - 1" class="my-6" style="border-color: var(--ods-line)">
          </div>
        </div>
        <div class="mt-4 text-center text-sm" style="color: var(--ods-ink-3)">
          {{ $t('clickToClose') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';

export default {
  name: 'OrderOverviewView',
  data() {
    return {
      loading: true,
      tables: {},
      completedTables: {},
      activeOrders: {},
      tableCounts: {
        active: 0,
        total: 0
      },
      knownTableIds: new Set(),
      language: 'ar',
      soundEnabled: false,
      isDarkMode: true,
      tableFilter: 'all',
      showModal: false,
      selectedTable: null
    };
  },
  computed: {
    ...mapState(['selectedRestaurantId', 'selectedRestaurantName']),
    
    connectionStatus() {
      return this.isConnected 
        ? this.$t('connected') 
        : this.$t('offline');
    },
    
    connectionClass() {
      return this.isConnected ? 'text-green-500 font-bold' : 'text-red-500 font-bold';
    },
    
    filteredTables() {
      if (this.tableFilter === 'active') {
        return this.tables;
      } else if (this.tableFilter === 'takeaway') {
        const takeaway = {};
        if (this.tables.takeaway) {
          takeaway.takeaway = this.tables.takeaway;
        }
        return takeaway;
      } else {
        // Show all active tables
        return this.tables;
      }
    },
    
    modalTitle() {
      if (!this.selectedTable) return this.$t('orderDetails');
      
      return this.selectedTable === 'takeaway' 
        ? `${this.$t('takeaway')} - ${this.$t('orderDetails')}`
        : `${this.$t('table')} ${this.selectedTable} - ${this.$t('orderDetails')}`;
    }
  },
  
  created() {
    // Set default theme
    document.documentElement.classList.add('dark');
  },
  
  mounted() {
    if (!this.selectedRestaurantId) {
      this.$router.push('/station-selection');
      return;
    }
    
    this.setupConnectionListener();
    this.listenForOrders();
  },
  
  beforeDestroy() {
    // Clean up Firebase listeners
    if (this.selectedRestaurantId && this.$firebase) {
      const db = this.$firebase.database();
      db.ref(`restaurants/${this.selectedRestaurantId}/orders`).off();
      db.ref('.info/connected').off();
    }
  },
  
  methods: {
    ...mapActions(['setSelectedRestaurant', 'setSelectedStation']),
    
    async setupConnectionListener() {
      try {
        const { ref, onValue } = await import('firebase/database');
        const db = this.$firebase.database();
        const connectedRef = ref(db, '.info/connected');
        
        onValue(connectedRef, (snap) => {
          this.isConnected = snap.val() === true;
        });
      } catch (error) {
        console.error('Error setting up connection listener:', error);
      }
    },
    
    async listenForOrders() {
      this.loading = true;
      try {
        const { ref, onValue } = await import('firebase/database');
        const db = this.$firebase.database();
        const ordersRef = ref(db, `restaurants/${this.selectedRestaurantId}/orders`);
        
        onValue(ordersRef, (snapshot) => {
          const allOrders = snapshot.val() || {};
          this.processOrders(allOrders);
          this.loading = false;
        });
      } catch (error) {
        console.error('Error listening for orders:', error);
        this.loading = false;
      }
    },
    
    processOrders(allOrders) {
      // Reset tables state
      this.tables = {};
      this.completedTables = {};
      this.activeOrders = {};
      
      // Group orders by table
      Object.entries(allOrders).forEach(([orderId, order]) => {
        // Skip orders without tableNumber
        if (!order.tableNumber) return;
        
        const tableNumber = order.tableNumber;
        
        // Check if order is completed or cancelled
        const isCompletedOrCancelled = 
          order.isCompleted === true || 
          order.status === 'completed' || 
          order.status === 'cancelled';
        
        // Add order to active or completed tables
        if (isCompletedOrCancelled) {
          // Add to completed tables
          if (!this.completedTables[tableNumber]) {
            this.completedTables[tableNumber] = [];
          }
          this.completedTables[tableNumber].push({ orderId, order });
        } else {
          // Add to active tables
          if (!this.tables[tableNumber]) {
            this.tables[tableNumber] = [];
          }
          this.tables[tableNumber].push({ orderId, order });
          
          // Keep track of active orders separately
          this.activeOrders[orderId] = order;
        }
      });
      
      // Update table counts
      this.tableCounts.active = Object.keys(this.tables).length;
      this.tableCounts.total = this.tableCounts.active + Object.keys(this.completedTables).length;
      
      // Play sound for new tables if enabled
      const hasNewTables = this.checkForNewTables();
      if (hasNewTables && this.soundEnabled) {
        this.playNewTableSound();
      }
    },
    
    checkForNewTables() {
      let hasNewTables = false;
      
      Object.keys(this.tables).forEach(tableNumber => {
        if (!this.knownTableIds.has(tableNumber)) {
          hasNewTables = true;
        }
      });
      
      return hasNewTables;
    },
    
    playNewTableSound() {
      if (!this.soundEnabled) return;
      
      try {
        const audio = new Audio('/SoundNoti.mp3');
        audio.volume = 0.8; // Set volume to 80%
        audio.play().catch(error => {
          console.error('Error playing new table sound:', error);
          // Fallback to original sound if SoundNoti.mp3 fails
          const fallbackAudio = new Audio('/KitchenSoundeffect.mp3');
          fallbackAudio.play().catch(fallbackError => 
            console.error('Error playing fallback sound:', fallbackError)
          );
        });
      } catch (error) {
        console.error('Error creating audio object:', error);
      }
    },
    
    hasNewAdditions(orders) {
      // Check if any items were added in the last 5 minutes
      const now = Date.now();
      const recentThreshold = 5 * 60 * 1000; // 5 minutes in milliseconds
      
      // Check all orders for this table
      return orders.some(({ order }) => {
        // Check all sections for recent additions
        return ['K', 'D', 'O'].some(section => {
          if (order[section] && order[section].items) {
            return order[section].items.some(item => {
              return item.addedAt && (now - item.addedAt) < recentThreshold;
            });
          }
          return false;
        });
      });
    },
    
    // Check if table has any new orders
    hasNewOrders(orders) {
      return orders.some(({ order }) => this.isOrderNew(order));
    },
    
    // Check if a specific order is new
    isOrderNew(order) {
      // Consider an order new if any section has status 'New'
      return ['K', 'D', 'O'].some(section => {
        if (!order[section]) return false;
        return order[section].status === 'New' || 
               (order[section].items && order[section].items.some(item => item.status === 'New'));
      });
    },
    
    getOrderStatusText(orders) {
      // Check if there are new orders or preparing orders
      if (this.hasNewOrders(orders)) {
        return this.language === 'ar' ? 'جديد' : 'New';
      }
      
      // Otherwise, get the most significant status
      const statusPriority = {
        'New': 1,
        'Preparing': 2,
        'Ready': 3,
        'Completed': 4,
        'Cancelled': 5
      };
      
      let highestStatus = { name: 'Completed', priority: 4 };
      
      orders.forEach(({ order }) => {
        ['K', 'D', 'O'].forEach(section => {
          if (!order[section]) return;
          
          const sectionStatus = order[section].status || 'New';
          const priority = statusPriority[sectionStatus] || 99;
          
          if (priority < highestStatus.priority) {
            highestStatus = { name: sectionStatus, priority };
          }
        });
      });
      
      // For Arabic, use مكتمل for completed status
      // Use the translation system
      const statusKey = `status${highestStatus.name.charAt(0).toUpperCase() + highestStatus.name.slice(1).toLowerCase()}`;
      return this.$t(statusKey) || highestStatus.name;
    },
    
    // Get class for table card based on status
    getTableStatusClass(orders) {
      const statusPriority = {
        'New': 1,
        'Preparing': 2,
        'Ready': 3,
        'Completed': 4,
        'Cancelled': 5
      };
      
      let highestStatus = { name: 'Completed', priority: 4 };
      
      orders.forEach(({ order }) => {
        ['K', 'D', 'O'].forEach(section => {
          if (!order[section]) return;
          
          const sectionStatus = order[section].status || 'New';
          const priority = statusPriority[sectionStatus] || 99;
          
          if (priority < highestStatus.priority) {
            highestStatus = { name: sectionStatus, priority };
          }
        });
      });
      
      // Apply classes based on status
      const statusName = highestStatus.name.toLowerCase();
      if (statusName === 'new') return 'status-border-new';
      if (statusName === 'preparing' || statusName === 'in progress') return 'status-border-preparing';
      if (statusName === 'ready') return 'status-border-ready';
      if (statusName === 'completed') return 'status-border-completed';
      if (statusName === 'cancelled') return 'status-border-cancelled';
      
      return 'status-border-new';
    },
    
    getTableStatusCounts(orders) {
      const counts = {
        new: 0,
        preparing: 0,
        ready: 0,
        completed: 0
      };
      
      orders.forEach(({ order }) => {
        // Check all sections for status counts
        ['K', 'D', 'O'].forEach(section => {
          if (order[section] && order[section].items) {
            const sectionStatus = order[section].status || 'New';
            
            // Count based on section status
            if (sectionStatus.toLowerCase() === 'new') {
              counts.new += order[section].items.length;
            } else if (sectionStatus.toLowerCase() === 'preparing' || sectionStatus.toLowerCase() === 'in progress') {
              counts.preparing += order[section].items.length;
            } else if (sectionStatus.toLowerCase() === 'ready') {
              counts.ready += order[section].items.length;
            } else if (sectionStatus.toLowerCase() === 'completed') {
              counts.completed += order[section].items.length;
            }
            
            // But also check individual item statuses which may override section status
            order[section].items.forEach(item => {
              if (item.status) {
                // Subtract from the section status count
                if (sectionStatus.toLowerCase() === 'new') {
                  counts.new -= 1;
                } else if (sectionStatus.toLowerCase() === 'preparing' || sectionStatus.toLowerCase() === 'in progress') {
                  counts.preparing -= 1;
                } else if (sectionStatus.toLowerCase() === 'ready') {
                  counts.ready -= 1;
                } else if (sectionStatus.toLowerCase() === 'completed') {
                  counts.completed -= 1;
                }
                
                // Add to the appropriate count based on item status
                if (item.status.toLowerCase() === 'new') {
                  counts.new += 1;
                } else if (item.status.toLowerCase() === 'preparing' || item.status.toLowerCase() === 'in progress') {
                  counts.preparing += 1;
                } else if (item.status.toLowerCase() === 'ready') {
                  counts.ready += 1;
                } else if (item.status.toLowerCase() === 'completed') {
                  counts.completed += 1;
                }
              }
            });
          }
        });
      });
      
      return counts;
    },
    
    getTableStationCounts(orders) {
      const counts = {
        K: 0,
        D: 0,
        O: 0
      };
      
      orders.forEach(({ order }) => {
        // Count items in each section
        ['K', 'D', 'O'].forEach(section => {
          if (order[section] && order[section].items) {
            // Count quantity for each item
            order[section].items.forEach(item => {
              counts[section] += (item.quantity || 1);
            });
          }
        });
      });
      
      return counts;
    },
    
    getSectionsWithItems(order) {
      const sections = {};
      
      // Check all sections for items
      ['K', 'D', 'O'].forEach(section => {
        if (order[section] && order[section].items && order[section].items.length > 0) {
          sections[section] = order[section];
        }
      });
      
      return sections;
    },
    
    getOrderTimeline(order) {
      // Collect timeline events
      const events = [];
      
      // Add order creation
      events.push({
        time: order.timestamp,
        type: 'created',
        description: 'Order created'
      });
      
      // Check all sections for item additions
      ['K', 'D', 'O'].forEach(section => {
        if (order[section] && order[section].items) {
          order[section].items.forEach(item => {
            if (item.addedAt) {
              events.push({
                time: item.addedAt,
                type: 'addition',
                description: `${this.$t('itemAdded')}: ${item.quantity || 1}x ${item.name || item.articleNumber}`,
                section
              });
            }
          });
        }
      });
      
      // Sort events by time (newest first)
      events.sort((a, b) => b.time - a.time);
      
      return events;
    },
    
    getLatestTimestamp(orders) {
      return orders.reduce((latest, { order }) => {
        return Math.max(latest, order.timestamp || 0);
      }, 0);
    },
    
    countTotalItems(orders) {
      let total = 0;
      
      orders.forEach(({ order }) => {
        // Count items in all sections
        ['K', 'D', 'O'].forEach(section => {
          if (order[section] && order[section].items) {
            order[section].items.forEach(item => {
              total += (item.quantity || 1);
            });
          }
        });
      });
      
      return total;
    },
    
    getStatusText(status) {
      const statusKey = `status${status.charAt(0).toUpperCase() + status.slice(1)}`;
      return this.$t(statusKey) || status;
    },
    
    getSectionStatusClass(status) {
      const normalized = status.toLowerCase();
      
      if (normalized === 'new') return 'status-new';
      if (normalized === 'preparing' || normalized === 'in progress') return 'status-preparing';
      if (normalized === 'ready') return 'status-ready';
      if (normalized === 'completed') return 'status-completed';
      if (normalized === 'cancelled') return 'status-cancelled';
      
      return 'status-new';
    },
    
    getStationName(sectionKey) {
      switch (sectionKey) {
        case 'K': return this.$t('kitchen');
        case 'D': return this.$t('desserts');
        case 'O': return this.$t('drinks');
        default: return sectionKey;
      }
    },
    
    getSectionIcon(sectionKey) {
      switch (sectionKey) {
        case 'K': return 'fas fa-utensils';
        case 'D': return 'fas fa-ice-cream';
        case 'O': return 'fas fa-glass-martini-alt';
        default: return 'fas fa-concierge-bell';
      }
    },
    
    getItemName(item) {
      // Try to match language preference
      if (this.language === 'ar' && item.name_ar) {
        return item.name_ar;
      }
      return item.name || item.articleNumber || 'Unknown Item';
    },
    
    // Get waiter name from order
    getWaiterName(order) {
      if (!order || !order.waiter) return 'Unknown';
      
      if (typeof order.waiter === 'object' && order.waiter.name) {
        return order.waiter.name;
      }
      
      if (typeof order.waiter === 'string') {
        return order.waiter;
      }
      
      return 'Unknown';
    },
    
    formatTime(timestamp) {
      if (!timestamp) return "";
      
      return new Date(parseInt(timestamp)).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    },
    
    showTableDetails(tableNumber) {
      this.selectedTable = tableNumber;
      this.showModal = true;
      // Mark this table as known (for new table highlighting)
      this.knownTableIds.add(tableNumber);
    },
    
    closeModal() {
      this.showModal = false;
      this.selectedTable = null;
    },
    
    changeTheme() {
      this.isDarkMode = !this.isDarkMode;
      if (this.isDarkMode) {
        document.documentElement.classList.add('dark');
        document.documentElement.classList.remove('light');
      } else {
        document.documentElement.classList.remove('dark');
        document.documentElement.classList.add('light');
      }
    },
    
    toggleLanguage() {
      this.language = this.language === 'ar' ? 'de' : 'ar';
      document.documentElement.setAttribute('lang', this.language);
    },
    
    toggleSound() {
      this.soundEnabled = !this.soundEnabled;
      
      // Play a short feedback sound if enabled
      if (this.soundEnabled) {
        this.playNewTableSound();
      }
    },
    
    goBack() {
      this.$router.push('/station-selection');
    },
    
    logOut() {
      this.$router.push('/');
    }
  }
};
</script>

<style scoped>
.tables-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding: 16px;
}

.table-card {
  transition: all 0.3s ease;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background-color: var(--ods-surface);
  color: var(--ods-ink);
  padding: 10px;
  position: relative;
  height: 130px;
  width: 130px;
  aspect-ratio: 1/1;
  border-radius: 0;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
}

.table-number {
  font-size: 22px;
  font-weight: bold;
  color: var(--ods-ink);
  text-align: center;
  background-color: rgba(0, 0, 0, 0.2);
  padding: 2px 6px;
  border-radius: 4px;
}

.new-badge {
  background-color: #22c55e;
  color: var(--ods-ink);
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: bold;
}

@keyframes cardPulse {
  0% {
    box-shadow: 0 0 8px rgba(34, 197, 94, 0.3);
  }
  50% {
    box-shadow: 0 0 15px rgba(34, 197, 94, 0.6);
  }
  100% {
    box-shadow: 0 0 8px rgba(34, 197, 94, 0.3);
  }
}

.new-card-pulse {
  animation: cardPulse 2s infinite;
}

.waiter-info {
  margin-top: 12px;
  font-size: 14px;
  color: var(--ods-ink-2);
  text-align: center;
  background-color: var(--ods-surface-2);
  padding: 4px;
  border-radius: 4px;
}

.order-status {
  margin-top: 10px;
  font-size: 15px;
  font-weight: 700;
  color: var(--ods-ink);
  text-align: center;
  background-color: rgba(0, 0, 0, 0.3);
  padding: 4px;
  border-radius: 4px;
}

/* Status border classes */
.status-border-new {
  border-left: 5px solid #22c55e;
  box-shadow: 0 0 10px rgba(34, 197, 94, 0.3);
}

.status-border-preparing {
  border-left: 5px solid #facc15;
  box-shadow: 0 0 10px rgba(250, 204, 21, 0.3);
}

.status-border-ready {
  border-left: 5px solid var(--ods-accent);
  box-shadow: 0 0 10px rgba(59, 130, 246, 0.3);
}

.status-border-completed {
  border-left: 5px solid var(--ods-ink-3);
  box-shadow: 0 0 10px rgba(107, 114, 128, 0.3);
}

.status-border-cancelled {
  border-left: 5px solid #ef4444;
  box-shadow: 0 0 10px rgba(239, 68, 68, 0.3);
}

.station-group {
  margin-bottom: 16px;
  padding: 12px;
  border-radius: var(--ods-radius);
}

.station-group-K {
  background-color: rgba(251, 146, 60, 0.1);
  border-left: 4px solid #FB923C;
}

.station-group-D {
  background-color: rgba(139, 92, 246, 0.1);
  border-left: 4px solid #8B5CF6;
}

.station-group-O {
  background-color: rgba(20, 184, 166, 0.1);
  border-left: 4px solid #14B8A6;
}

.station-header {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  font-weight: 700;
}

.station-header-K {
  color: #FB923C;
}

.station-header-D {
  color: #8B5CF6;
}

.station-header-O {
  color: #14B8A6;
}

/* Status badges */
.status-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 700;
}

.status-new {
  background-color: var(--ods-accent);
  color: var(--ods-ink);
}

.status-preparing {
  background-color: #FACC15;
  color: var(--ods-ink);
}

.status-ready {
  background-color: #22C55E;
  color: var(--ods-ink);
}

.status-completed {
  background-color: var(--ods-ink-3);
  color: var(--ods-ink);
}

.status-cancelled {
  background-color: #EF4444;
  color: var(--ods-ink);
}

/* Visual timeline for order changes */
.order-timeline {
  margin-top: 24px;
  padding-left: 24px;
  position: relative;
}

.order-timeline:before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 2px;
  background-color: var(--ods-surface-3);
}

.timeline-item {
  position: relative;
  padding-bottom: 16px;
}

.timeline-item:before {
  content: '';
  position: absolute;
  left: -28px;
  top: 0;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: var(--ods-accent);
}

.timeline-time {
  font-size: 0.75rem;
  color: var(--ods-ink-2);
  margin-bottom: 4px;
}

.timeline-content {
  background-color: rgba(59, 130, 246, 0.1);
  padding: 8px 12px;
  border-radius: 4px;
  border-left: 3px solid var(--ods-accent);
}

/* Modal */
.modal {
  display: block;
  position: fixed;
  z-index: 1000;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: auto;
  background-color: rgba(0, 0, 0, 0.8);
  cursor: pointer;
  transition: background-color 0.3s;
}

.modal-content {
  margin: 5% auto;
  padding: 20px;
  width: 90%;
  max-width: 900px;
  border-radius: var(--ods-radius);
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
  cursor: default;
  max-height: 90vh;
  overflow-y: auto;
  background-color: var(--ods-surface);
  color: var(--ods-ink);
  border: 1px solid var(--ods-line);
}
</style>